import * as React from "react";
import { useEffect } from "react";
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useRequireAuth } from "@/store/authStore";
import { Loader, LogOut } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { useAuthStore } from "@/store/authStore";
import { useLogout } from "@/hooks/useLogout";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/UserAvatar";
import { useOrganizationStore } from "@/store/organizationStore";
import { OnboardingBanner } from "@/modules/onboarding/OnboardingBanner";

export interface BreadcrumbItem {
  label: string;
  href?: string;
  current?: boolean;
}

export default function Layout({
  children,
  className,
  breadcrumbs = [],
}: {
  children: React.ReactNode;
  className?: string;
  breadcrumbs?: BreadcrumbItem[];
}) {
  // Use the new hook that requires authentication
  const { isLoading } = useRequireAuth();
  const firstName = useAuthStore((state) => state.user?.firstName || "");
  const lastName = useAuthStore((state) => state.user?.lastName || "");
  const email = useAuthStore((state) => state.user?.email || "");
  const profilePictureUrl = useAuthStore(
    (state) => state.user?.profilePictureUrl ?? null,
  );
  const organization = useOrganizationStore((state) => state.organization);
  const fetchOrganization = useOrganizationStore(
    (state) => state.fetchOrganization,
  );
  const orgLoading = useOrganizationStore((state) => state.loading);
  const { logout, isLoggingOut } = useLogout();

  // Fetch organization data if not already loaded
  useEffect(() => {
    if (!organization && !orgLoading) {
      fetchOrganization();
    }
  }, [organization, orgLoading, fetchOrganization]);

  const name = React.useMemo(() => {
    if (firstName || lastName) {
      return `${firstName} ${lastName}`;
    }
    return email;
  }, [firstName, lastName, email]);

  // Get user initials for the avatar
  const userInitials = React.useMemo(() => {
    const firstInitial = firstName ? firstName.charAt(0) : email.charAt(0);
    const lastInitial = lastName ? lastName.charAt(0) : "";
    return (firstInitial + lastInitial).toUpperCase();
  }, [firstName, lastName, email]);

  // Restore the sidebar's collapsed state across navigations so going back
  // doesn't force it open again. The cookie is written by SidebarProvider but
  // never read on mount, and each page remounts its own Layout.
  const sidebarDefaultOpen = !document.cookie
    .split("; ")
    .includes("sidebar_state=false");

  // Show loading indicator while authenticating or fetching organization data
  if (isLoading || (!organization && orgLoading)) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Loader className="animate-spin" />
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100vw",
        maxWidth: "100%",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <SidebarProvider
        defaultOpen={sidebarDefaultOpen}
        className="max-h-screen"
      >
        <AppSidebar />
        <SidebarInset>
          <OnboardingBanner />
          {breadcrumbs.length > 0 && (
            <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
              <SidebarTrigger className="-ml-1" />
              <BackButton />
              <Separator orientation="vertical" className="mr-2 h-4" />
              <Breadcrumb>
                <BreadcrumbList>
                  {breadcrumbs.map((item, index) => (
                    <React.Fragment key={item.label}>
                      <BreadcrumbItem
                        className={index === 0 ? "hidden md:block" : ""}
                      >
                        {item.current ? (
                          <BreadcrumbPage>{item.label}</BreadcrumbPage>
                        ) : (
                          <BreadcrumbLink href={item.href || "#"}>
                            {item.label}
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>
                      {index < breadcrumbs.length - 1 && (
                        <BreadcrumbSeparator
                          className={index === 0 ? "hidden md:block" : ""}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>

              {/* User dropdown - positioned to the right */}
              <div className="flex-1"></div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 gap-2 px-2">
                    <UserAvatar
                      className="h-6 w-6"
                      profilePictureUrl={profilePictureUrl}
                      name={name}
                      initials={userInitials}
                    />
                    <span>{name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => logout()}
                    disabled={isLoggingOut}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{isLoggingOut ? "Logging out..." : "Logout"}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </header>
          )}
          <main
            className={cn("flex-1 min-h-0 min-w-0 overflow-y-auto", className)}
          >
            {children}
          </main>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
}
